import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import RCLogo from '../components/Logo';
import { isAuthenticated } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import useDocumentTitle from '../lib/useDocumentTitle';
import { demoAPI } from '../lib/api';
import {
  Sparkles, Image, Video, Calendar, ArrowRight,
  Check, Clock, ChevronRight,
  // v1.5. Pricing-section icons (trust strip + Team card)
  Lock, ShieldCheck, RefreshCw, CreditCard, Users,
  // v1.6. Comparison-section icons (DIY / Agency cards)
  Wrench, Building2, X as XIcon,
  // v1.7. Viral-demo paste-bar
  Loader2, Link2, AlertCircle,
  // Gallery sound toggle
  Volume2, VolumeX,
} from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // SEO. Title is also set in index.html for crawlers; this hook
  // updates the browser tab + history label client-side too.
  useDocumentTitle('Real Content. Vom Inserat zum Immobilienvideo in 30 Sekunden');

  // v1.7. Viral-demo paste-bar state. The whole "paste Willhaben URL
  // → see a reel of YOUR listing → sign up" funnel hangs off these
  // four values. Submit posts to /public/demo/render (1/hr per IP,
  // 50/day global, 24h URL-dedup), then navigates to /demo/<token>
  // where the share page polls Creatomate and shows the rendered
  // video. `demoQuotaHit` flag flips to true on a 429 so the error
  // block surfaces a prominent signup CTA instead of just text.
  const [demoUrl, setDemoUrl] = useState('');
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoError, setDemoError] = useState('');
  const [demoQuotaHit, setDemoQuotaHit] = useState(false);
  // Which gallery video currently plays with sound (only one at a time).
  const [unmutedSlug, setUnmutedSlug] = useState(null);
  // One-shot guard for the ?inserat= auto-start (prefix-URL funnel).
  const autoStartedRef = useRef(false);

  const startDemoRender = async (url) => {
    setDemoLoading(true);
    setDemoError('');
    setDemoQuotaHit(false);
    try {
      const { data } = await demoAPI.startRender(url);
      navigate(`/demo/${data.token}`);
    } catch (err) {
      // Surface the backend's user-facing message (ships German
      // strings tuned for Austrian Maklers – "ungültige URL", "zu
      // wenige Bilder", "Willhaben nicht erreichbar", etc.).
      // 429 = quota hit (per-IP hourly OR global daily). The backend
      // returns the funnel-friendly copy directly; we just flip the
      // flag so the error block renders the inline signup CTA.
      const msg = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 429) {
        setDemoQuotaHit(true);
        setDemoError(msg || t('landing.demoErrorRateLimit') || 'Demo-Limit erreicht.');
      } else {
        setDemoError(msg || t('landing.demoErrorGeneric') || 'Etwas ist schiefgelaufen. Bitte erneut versuchen.');
      }
      setDemoLoading(false);
    }
  };

  const handleDemoSubmit = (e) => {
    e?.preventDefault();
    const url = demoUrl.trim();
    if (!url) return;
    startDemoRender(url);
  };

  // v1.8. Prefix-URL funnel: real-content.at/<willhaben-url> lands here
  // as /?inserat=<encoded-url> (see the catch-all route in App.js).
  // Prefill the paste bar AND kick the render off immediately — the
  // whole marketing pitch is "prefix the URL, video starts". One-shot
  // via ref so re-renders can't double-submit against the demo quota.
  useEffect(() => {
    if (autoStartedRef.current) return;
    const prefill = new URLSearchParams(window.location.search).get('inserat');
    if (prefill) {
      autoStartedRef.current = true;
      setDemoUrl(prefill);
      startDemoRender(prefill);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirect if already authenticated — EXCEPT when arriving through the
  // prefix-URL funnel (?inserat=). A logged-in Makler who prefixed a
  // listing wants the instant demo, not their dashboard.
  useEffect(() => {
    const hasPrefill = new URLSearchParams(window.location.search).has('inserat');
    if (isAuthenticated() && !hasPrefill) {
      navigate('/dashboard');
    }
  }, [navigate]);

  const features = [
    {
      icon: Image,
      title: t('landing.feature1Title'),
      description: t('landing.feature1Desc'),
    },
    {
      icon: Sparkles,
      title: t('landing.feature2Title'),
      description: t('landing.feature2Desc'),
    },
    {
      icon: Video,
      title: t('landing.feature3Title'),
      description: t('landing.feature3Desc'),
    },
    {
      icon: Calendar,
      title: t('landing.feature4Title'),
      description: t('landing.feature4Desc'),
    },
  ];

  // Pricing-section feature lists. Three tiers, each focused on the
  // promise that matters most for that audience: Free = "try it",
  // Solo = "do all your own listings", Team = "share the bill".
  const freeFeatures = [
    t('pricing.features.freeVideos'),     // 5 Videos / Monat
    t('pricing.features.freeTemplates'),  // Alle Templates
    t('pricing.features.freeSupport'),    // E-Mail-Support
  ];
  const starterFeatures = [
    t('landing.starterFeature1') || '5 Videos pro Monat',
    t('landing.starterFeature2') || 'Alle Templates inklusive Premium',
    t('landing.starterFeature3') || 'Inserate-Import aus Justimmo & Willhaben',
    t('landing.starterFeature4') || 'KI-Voiceover, Musik & dein Branding',
    t('landing.starterFeature5') || 'Full HD 1080p',
  ];
  const soloFeatures = [
    t('landing.soloFeature1') || 'Täglich 1 Video, automatisch erstellt und gepostet',
    t('landing.soloFeature2') || 'Auto-Import neuer Inserate aus Justimmo & Willhaben',
    t('landing.soloFeature3') || 'Postet von allein auf 6 Plattformen',
    t('landing.soloFeature4') || '12-Stunden-Vorschau: du gibst nur frei',
    t('landing.soloFeature5') || 'Priority-Support',
  ];
  const teamFeatures = [
    t('landing.teamFeature1') || 'Alles aus Autopilot, für jeden Mitarbeiter',
    t('landing.teamFeature2') || 'Pro Makler ein eigenes Justimmo-Profil',
    t('landing.teamFeature3') || 'Mitglieder jederzeit hinzufügen. Anteilig abgerechnet',
    t('landing.teamFeature4') || 'Eine Rechnung für die ganze Agentur',
  ];

  // Showcase gallery: nine real renders, each of a DIFFERENT property with
  // its own coherent photo set (see GALLERY at the bottom of this file).
  const unmuteGallery = (slug) => setUnmutedSlug((cur) => (cur === slug ? null : slug));

  return (
    <div className="makler min-h-screen bg-[#f7f3ea] text-[#23211a]">
      {/* Makler-Premium (2026-09, pass 2): warm ivory paper, serif
          display type, deep pine green as the single accent, brass
          labels. Reads "Premium-Exposé", not "developer tool". The
          dark violet stays app-internal; here nothing is dark or
          gradient. */}

      {/* Navigation. Blur stays here and ONLY here — glass belongs on
          the sticky header, not on content cards. */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#f7f3ea]/85 border-b border-[#e3dccb]">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <RCLogo size="md" tone="ink" />
          <div className="flex items-center gap-4">
            <a href="#pricing" className="text-[#6f6a5c] hover:text-[#23211a] transition-colors hidden sm:block">
              {t('landing.pricing')}
            </a>
            <a href="#features" className="text-[#6f6a5c] hover:text-[#23211a] transition-colors hidden sm:block">
              {t('landing.features')}
            </a>
            <Link to="/auth" className="hidden sm:block">
              <Button variant="ghost" className="text-[#6f6a5c] hover:text-[#23211a] hover:bg-[#efe9db]">
                {t('landing.signIn')}
              </Button>
            </Link>
            <Link to="/auth">
              <Button className="rounded-[3px] bg-[#1f4d3a] hover:bg-[#163a2c] text-[#f7f3ea]">
                {t('landing.getStarted')}
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section. Two-column Exposé layout: left is the pitch +
          the paste-bar funnel, right is an actual rendered reel in a
          white gallery mat like a mounted property photo. Left-aligned
          serif display type; the "In 30 Sekunden." clause is set as a
          deep-green inked stamp. */}
      <section className="relative px-4 pt-32 pb-20 lg:pt-40 lg:pb-24">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-12 lg:gap-16 items-center">
        <div className="z-10">
          <p className="kicker mb-6">{t('landing.aiPowered')}</p>

          {/* Hero clauses split on `.`, one per line; the last clause
              gets the stamp. Keep the copy a multi-clause sentence
              ending in periods for consistent rendering. */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-[1.08]">
            {t('landing.hero')
              .split('.')
              .map((s) => s.trim())
              .filter(Boolean)
              .map((clause, i, arr) => {
                const isLast = i === arr.length - 1;
                return (
                  <span key={i} className="block">
                    {isLast ? (
                      <span className="stamp mt-2">{clause}.</span>
                    ) : (
                      <span className="text-[#23211a]">{clause}.</span>
                    )}
                  </span>
                );
              })}
          </h1>

          <p className="text-lg text-[#6f6a5c] max-w-xl mb-8 leading-relaxed">
            {t('landing.heroSubtitle')}
          </p>

          {/* v1.7. Viral-demo paste-bar. The "sales cheat code": a
              visitor pastes a Willhaben URL and we render one
              polished reel of THEIR listing in ~60 seconds. No
              signup needed. The share page then funnels them to
              create an account to claim the video. Lives above the
              "Kostenlos starten" CTA because pasting a URL is a
              much stronger first action than another click-through
              to a signup form. */}
          <form
            onSubmit={handleDemoSubmit}
            className="max-w-xl mb-4"
            data-testid="hero-demo-form"
          >
            <div className="flex items-center gap-2 p-2 rounded-[3px] bg-white border border-[#d8d0bd] focus-within:border-[#1f4d3a] shadow-sm transition-colors">
              <Link2 className="w-5 h-5 text-[#9c9585] ml-3 shrink-0" />
              <input
                type="url"
                value={demoUrl}
                onChange={(e) => { setDemoUrl(e.target.value); if (demoError) setDemoError(''); }}
                placeholder={t('landing.demoPlaceholder') || 'Willhaben-URL einfügen…'}
                className="flex-1 bg-transparent border-none outline-none text-[#23211a] placeholder-[#9c9585] text-base py-2 min-w-0"
                disabled={demoLoading}
                data-testid="hero-demo-input"
              />
              <Button
                type="submit"
                disabled={demoLoading || !demoUrl.trim()}
                data-testid="hero-demo-submit"
                className="rounded-[3px] bg-[#1f4d3a] hover:bg-[#163a2c] text-[#f7f3ea] px-5 py-2.5 whitespace-nowrap disabled:opacity-60"
              >
                {demoLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('landing.demoLoading') || 'Wir rendern…'}
                  </>
                ) : (
                  <>
                    {t('landing.demoCta') || 'Reel erstellen'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
            {demoError && (
              <div
                // Quota-hit variant gets violet accent + a prominent
                // signup CTA: the funnel point is to convert the
                // "blocked" moment into a signup, not just show a
                // dead-end error. Plain errors stay rose/red because
                // they're recoverable (try a different URL, retry, etc).
                className={
                  demoQuotaHit
                    ? 'mt-3 px-4 py-3 rounded-[3px] bg-[#e9efe7] border border-[#1f4d3a]/30 text-[#23211a] text-sm'
                    : 'mt-3 px-4 py-2.5 rounded-[3px] bg-[#f7e8e3] border border-[#b3543f]/40 text-[#8c3a29] text-sm flex items-start gap-2'
                }
                role="alert"
                data-testid="hero-demo-error"
              >
                {demoQuotaHit ? (
                  <>
                    <div className="flex items-start gap-2 mb-3">
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-[#1f4d3a]" />
                      <span>{demoError}</span>
                    </div>
                    <Link to="/auth" className="block">
                      <Button
                        type="button"
                        data-testid="hero-demo-signup-cta"
                        className="rounded-[3px] w-full bg-[#1f4d3a] hover:bg-[#163a2c] text-[#f7f3ea]"
                      >
                        {t('landing.demoQuotaSignupCta') || 'Kostenloses Konto erstellen'}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{demoError}</span>
                  </>
                )}
              </div>
            )}
            <p className="text-xs text-[#9c9585] mt-2.5">
              {t('landing.demoHint') || 'Kostenlos · Kein Account nötig · In ~60 Sekunden fertig'}
            </p>
          </form>

          {/* Second path: create a free account instead of pasting a
              URL. A quiet text link, not a second competing button —
              the paste bar is THE primary action. */}
          <p className="text-sm text-[#6f6a5c] mb-8">
            {t('landing.demoOr') || 'oder'}{' '}
            <Link
              to="/auth"
              data-testid="hero-get-started-button"
              className="text-[#1f4d3a] hover:text-[#163a2c] underline underline-offset-4 decoration-[#1f4d3a]/40"
            >
              {t('landing.startFree')}
            </Link>
          </p>

          {/* Risk-reversal, one plain line. No green check icons. */}
          <p className="text-xs text-[#9c9585] mb-6">
            {t('landing.trustNoCard') || 'Keine Kreditkarte nötig'}
            {' · '}
            {t('landing.trustFreeForever') || '5 Videos gratis, jeden Monat'}
            {' · '}
            {t('landing.trustCancel') || 'Jederzeit kündbar'}
          </p>

          {/* Target platforms, one quiet label line. */}
          <p className="text-[11px] uppercase tracking-[0.14em] text-[#9c9585]">
            Instagram · TikTok · Facebook · YouTube · LinkedIn · X
          </p>
        </div>

        {/* Right column: one real render presented like a mounted
            property photo — white gallery mat, warm hairline, serif
            caption. Concrete proof beats decoration. Hidden below lg
            so the paste bar stays above the fold on phones. */}
        <div className="hidden lg:block">
          <div className="bg-white border border-[#e3dccb] shadow-[0_10px_30px_-18px_rgba(35,33,26,0.4)] p-4 pb-3 rotate-[0.6deg]">
            <div className="aspect-[9/16] overflow-hidden bg-[#efe9db]">
              <video
                src={galleryVideoUrl(GALLERY[0])}
                poster={galleryPosterUrl(GALLERY[0])}
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
              />
            </div>
            <p
              className="text-center text-sm text-[#6f6a5c] pt-3"
              style={{ fontFamily: "'Fraunces', Georgia, serif" }}
            >
              {t('landing.heroFrameCaption') || 'Direkt aus dem Inserat gerendert. In 30 Sekunden.'}
            </p>
          </div>
        </div>
        </div>
      </section>

      {/* Examples Section - Real demo videos */}
      <section className="py-24 px-4 border-t border-[#e3dccb]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 max-w-2xl">
            <p className="kicker mb-4">01 · {t('landing.kickerExamples') || 'Beispiele'}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#23211a] mb-3">
              {t('landing.seeCreations')}
            </h2>
            <p className="text-[#6f6a5c] text-lg">{t('landing.realExamples')}</p>
          </div>

          {/* Six 9:16 renders, one property each. Videos play only while
              on screen (six simultaneous decodes would choke phones);
              tapping a video unmutes it and mutes the rest. Caption sits
              below the frame like an exposé card, since the render itself
              already carries the listing text. */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-8 sm:gap-x-6 max-w-5xl mx-auto">
            {GALLERY.map((item, i) => (
              <figure
                key={item.slug}
                // Odd count on the 2-column phone grid: centre the last tile
                // at normal tile width instead of leaving it hanging left.
                className={GALLERY.length % 2 === 1 && i === GALLERY.length - 1
                  ? 'm-0 col-span-2 w-1/2 justify-self-center md:col-span-1 md:w-auto md:justify-self-auto'
                  : 'm-0'}
              >
                <GalleryVideo
                  item={item}
                  unmuted={unmutedSlug === item.slug}
                  onToggleSound={() => unmuteGallery(item.slug)}
                />
                <figcaption className="pt-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8f6f3a] mb-1">
                    {t('landing.galleryTemplate', { name: item.template })}
                  </p>
                  <h3 className="text-base sm:text-lg font-semibold text-[#23211a] leading-snug">{item.title}</h3>
                  <p className="text-xs sm:text-sm text-[#6f6a5c]">{item.place} · {item.price}</p>
                </figcaption>
              </figure>
            ))}
          </div>
          <p className="text-center text-xs text-[#9c9585] mt-6">
            {t('landing.tapToUnmute') || 'Tippe auf ein Video, um den AI-Voiceover zu hören'}
          </p>
        </div>
      </section>

      {/* Features Section. A numbered ledger instead of the four
          icon-in-tinted-box hover-lift cards: oversized outline
          numerals, hairline rules, text left. Reads like positions
          on a Leistungsverzeichnis, not like a template's feature
          grid. */}
      <section id="features" className="py-24 px-4 border-t border-[#e3dccb]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 max-w-2xl">
            <p className="kicker mb-4">02 · {t('landing.kickerWhy') || 'Warum wir'}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#23211a] mb-3">
              {t('landing.everythingYouNeed')}
            </h2>
            <p className="text-[#6f6a5c] text-lg">{t('landing.forProfessionals')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2">
            {features.map((feature, index) => (
              <div
                key={index}
                data-testid={`feature-card-${index}`}
                className={`flex gap-6 py-8 pr-6 border-t border-[#e3dccb] ${index % 2 === 0 ? 'md:border-r md:border-[#e3dccb]' : 'md:pl-8'}`}
              >
                <span
                  className="text-4xl font-bold leading-none text-[#c7a869] select-none shrink-0"
                  style={{ fontFamily: "'Fraunces', Georgia, serif" }}
                  aria-hidden="true"
                >
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3 className="text-xl font-semibold text-[#23211a] mb-2">{feature.title}</h3>
                  <p className="text-[#6f6a5c]">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section. V1.6
          Sandwiched between Features and How-It-Works to anchor the
          buying decision. The visitor now knows what we do (Features);
          this section frames the alternatives (DIY / agency) honestly
          so we win on combined math: time saved + cost vs. agency +
          consistency vs. DIY. The Real Content card has a violet glow
          + "Empfehlung" badge to be the visual anchor of the row. */}
      <section className="py-24 px-4 border-t border-[#e3dccb]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 max-w-2xl">
            <p className="kicker mb-4">03 · {t('landing.kickerCompare') || 'Der Vergleich'}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#23211a] mb-3">
              {t('landing.compareTitle')}
            </h2>
            <p className="text-[#6f6a5c] text-lg">
              {t('landing.compareSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* DIY card */}
            <div className="bg-white border border-[#e3dccb] border-t-2 border-t-[#d8d0bd] p-7 flex flex-col">
              <div className="flex items-center gap-3 mb-4">
                <Wrench className="w-5 h-5 text-[#9c9585]" />
                <h3 className="text-xl font-bold text-[#23211a]">{t('landing.compareDiyTitle')}</h3>
              </div>
              <div className="mb-4">
                <div className="text-2xl font-bold text-[#23211a]">{t('landing.compareDiyPrice')}</div>
                <div className="text-sm text-[#8a8474] mt-1 inline-flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  {t('landing.compareDiyTime')}
                </div>
              </div>
              <ul className="space-y-2.5 mb-2 flex-1">
                {[1, 2, 3].map((n) => (
                  <li key={n} className="flex items-start gap-2.5 text-sm text-[#6f6a5c]">
                    <XIcon className="w-4 h-4 text-[#b3543f]/70 mt-0.5 shrink-0" />
                    <span>{t(`landing.compareDiyCon${n}`)}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Agency card */}
            <div className="bg-white border border-[#e3dccb] border-t-2 border-t-[#d8d0bd] p-7 flex flex-col">
              <div className="flex items-center gap-3 mb-4">
                <Building2 className="w-5 h-5 text-[#9c9585]" />
                <h3 className="text-xl font-bold text-[#23211a]">{t('landing.compareAgencyTitle')}</h3>
              </div>
              <div className="mb-4">
                <div className="text-2xl font-bold text-[#23211a]">{t('landing.compareAgencyPrice')}</div>
                <div className="text-sm text-[#8a8474] mt-1 inline-flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  {t('landing.compareAgencyTime')}
                </div>
              </div>
              <ul className="space-y-2.5 mb-2 flex-1">
                {[1, 2, 3].map((n) => (
                  <li key={n} className="flex items-start gap-2.5 text-sm text-[#6f6a5c]">
                    <XIcon className="w-4 h-4 text-[#b3543f]/70 mt-0.5 shrink-0" />
                    <span>{t(`landing.compareAgencyCon${n}`)}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Real Content card. The anchor is the one inverted card:
                deep pine green with ivory text and a brass tag. Quietly
                unmissable, no scale, no glow. */}
            <div className="bg-[#1f4d3a] border-t-2 border-t-[#c7a869] p-7 relative flex flex-col shadow-[0_16px_40px_-22px_rgba(31,77,58,0.6)]">
              <div className="absolute top-4 right-4 px-2 py-0.5 bg-[#c7a869] rounded-[2px] text-[10px] font-semibold uppercase tracking-wider text-[#23211a]">
                {t('landing.compareRcBadge')}
              </div>
              <div className="flex items-center gap-3 mb-4">
                <h3 className="text-xl font-bold text-[#f7f3ea]">{t('landing.compareRcTitle')}</h3>
              </div>
              <div className="mb-4">
                <div className="text-2xl font-bold text-[#f7f3ea]">{t('landing.compareRcPrice')}</div>
                <div className="text-sm text-[#cfdcd0] mt-1 inline-flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  {t('landing.compareRcTime')}
                </div>
              </div>
              <ul className="space-y-2.5 mb-5 flex-1">
                {[1, 2, 3].map((n) => (
                  <li key={n} className="flex items-start gap-2.5 text-sm text-[#f2efe6]">
                    <Check className="w-4 h-4 text-[#c7a869] mt-0.5 shrink-0" />
                    <span>{t(`landing.compareRcPro${n}`)}</span>
                  </li>
                ))}
              </ul>
              <Link to="/auth" className="block mt-auto">
                <Button
                  data-testid="landing-compare-rc-cta"
                  className="rounded-[3px] w-full bg-[#f7f3ea] hover:bg-white text-[#1f4d3a] font-semibold"
                >
                  {t('landing.startFree')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works. Styled as a render pipeline: one continuous
          rail with square station markers, steps hanging off it. On
          mobile the rail runs vertically down the left edge. Replaces
          the ghost-number cards + floating chevrons. */}
      <section className="py-24 px-4 border-t border-[#e3dccb]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-14 max-w-2xl">
            <p className="kicker mb-4">04 · {t('landing.kickerHow') || 'Ablauf'}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#23211a] mb-3">
              {t('landing.howItWorksTitle')}
            </h2>
            <p className="text-[#6f6a5c] text-lg">{t('landing.threeSteps')}</p>
          </div>

          <div className="relative">
            {/* The rail: horizontal on md+, vertical on mobile. */}
            <div className="hidden md:block absolute top-[7px] left-0 right-0 h-px bg-[#d8d0bd]" aria-hidden="true" />
            <div className="md:hidden absolute top-0 bottom-0 left-[7px] w-px bg-[#d8d0bd]" aria-hidden="true" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
              {[
                { title: t('landing.step1Title'), desc: t('landing.step1Desc') },
                { title: t('landing.step2Title'), desc: t('landing.step2Desc') },
                { title: t('landing.step3Title'), desc: t('landing.step3Desc') },
              ].map((item, index) => (
                <div key={index} className="relative pl-10 md:pl-0 md:pt-10">
                  {/* Station marker: filled square on the rail. */}
                  <span
                    className="absolute left-0 top-0 md:top-0 w-[15px] h-[15px] bg-[#f7f3ea] border-2 border-[#1f4d3a]"
                    aria-hidden="true"
                  />
                  <p className="text-xs font-semibold text-[#8f6f3a] uppercase tracking-[0.14em] mb-2">
                    {String(index + 1).padStart(2, '0')} / 03
                  </p>
                  <h3 className="text-xl font-semibold text-[#23211a] mb-2">{item.title}</h3>
                  <p className="text-[#6f6a5c]">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section. V1.5
          Three tiers (Free / Solo / Team) + a trust strip above the
          cards + a small "Häufige Fragen" block below. Goal: a Makler
          landing here from a Google ad has every objection answered in
          one screen. Pricing, what they get, no-contract messaging,
          DSGVO trust signals. Without bouncing to a separate page. */}
      <section id="pricing" className="py-20 sm:py-24 px-4 border-t border-[#e3dccb]">
        <div className="max-w-6xl mx-auto">
          <div className="mb-10 sm:mb-12 max-w-2xl">
            <p className="kicker mb-4">05 · {t('landing.kickerPricing') || 'Preise'}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#23211a] mb-3">
              {t('landing.simplePricing')}
            </h2>
            <p className="text-[#6f6a5c] text-base sm:text-lg">
              {t('landing.pricingSubtitle')
                || 'Monatliche Abrechnung. Jederzeit kündbar. Keine Mindestlaufzeit.'}
            </p>
          </div>

          {/* Trust strip. Four small badges arranged in a row above
              the pricing cards. Picks the four objections that come up
              most: contract length, cancel-anytime, where billing
              happens (EU), how payment is handled (Stripe). */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10 sm:mb-12">
            {[
              {
                icon: RefreshCw,
                label: t('landing.trustMonthly') || 'Monatlich abrechnen',
                detail: t('landing.trustMonthlyDetail') || 'Keine Jahresverträge.',
              },
              {
                icon: ShieldCheck,
                label: t('landing.trustCancelAnytime') || 'Jederzeit kündbar',
                detail: t('landing.trustCancelAnytimeDetail') || 'Ein Klick im Kundenportal.',
              },
              {
                icon: Lock,
                label: t('landing.trustDsgvo') || 'DSGVO-konform',
                detail: t('landing.trustDsgvoDetail') || 'Server in der EU. Daten-Export jederzeit.',
              },
              {
                icon: CreditCard,
                label: t('landing.trustStripe') || 'Sichere Zahlung',
                detail: t('landing.trustStripeDetail') || 'Abgewickelt über Stripe (PCI-DSS Level 1).',
              },
            ].map((badge, i) => (
              <div
                key={i}
                className="px-4 py-3 border-l-2 border-[#c7a869]"
              >
                <p className="text-sm font-medium text-[#23211a] leading-tight">{badge.label}</p>
                <p className="text-xs text-[#8a8474] mt-1 leading-snug">{badge.detail}</p>
              </div>
            ))}
          </div>

          {/* Four pricing tiers. On mobile: stacked. md: 2-up. lg: 4-up.
              Solo is the visual anchor (border + glow) because it's
              the most-frequently-bought plan; Starter is the low-friction
              paid entry; Team gets a teal accent as the agency option. */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Free Plan */}
            <div className="bg-white border border-[#e3dccb] border-t-2 border-t-[#d8d0bd] p-7 flex flex-col">
              <div className="mb-5">
                <h3 className="text-xl font-bold text-[#23211a] mb-1">{t('pricing.free')}</h3>
                <p className="text-sm text-[#8a8474]">{t('landing.perfectStart')}</p>
              </div>
              <div className="mb-5">
                <span className="text-4xl sm:text-5xl font-bold text-[#23211a]">&euro;0</span>
                <span className="text-[#8a8474] text-sm"> / {t('pricing.month') || 'Monat'}</span>
              </div>
              <ul className="space-y-2.5 mb-6 flex-1">
                {freeFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2.5 text-sm text-[#6f6a5c]">
                    <Check className="w-4 h-4 text-[#9c9585] mt-0.5 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Link to="/auth" className="block mt-auto">
                <Button
                  variant="outline"
                  className="w-full rounded-[3px] border-[#d8d0bd] hover:border-[#1f4d3a] hover:bg-transparent text-[#23211a]"
                  data-testid="landing-pricing-free"
                >
                  {t('landing.getStartedFree') || 'Kostenlos starten'}
                </Button>
              </Link>
            </div>

            {/* Starter Plan. €29 casual tier: video volume without the
                automation machine. The low-friction first paid step for
                Maklers who balk at €99. */}
            <div className="bg-white border border-[#e3dccb] border-t-2 border-t-[#1f4d3a]/50 p-7 flex flex-col">
              <div className="mb-5">
                <h3 className="text-xl font-bold text-[#23211a] mb-1">Starter</h3>
                <p className="text-sm text-[#8a8474]">
                  {t('landing.forOccasional') || 'Videos erstellen, selbst posten'}
                </p>
              </div>
              <div className="mb-5 flex items-baseline gap-2 flex-wrap">
                <span className="text-4xl sm:text-5xl font-bold text-[#23211a]">&euro;29</span>
                <span className="text-[#8a8474] text-sm"> / {t('pricing.month') || 'Monat'}</span>
                <s className="text-lg font-medium text-[#9c9585]">&euro;49</s>
              </div>
              <ul className="space-y-2.5 mb-6 flex-1">
                {starterFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2.5 text-sm text-[#4a463b]">
                    <Check className="w-4 h-4 text-[#1f4d3a] mt-0.5 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
                <li className="flex items-start gap-2.5 text-sm text-[#8a8474]">
                  <XIcon className="w-4 h-4 text-[#b3543f]/60 mt-0.5 shrink-0" />
                  <span>{t('pricing.features.starterNoAutopost') || 'Kein Auto-Posting: das übernimmt Autopilot'}</span>
                </li>
              </ul>
              <Link to="/auth" className="block mt-auto">
                <Button
                  data-testid="landing-pricing-starter"
                  variant="outline"
                  className="w-full rounded-[3px] border-[#1f4d3a]/50 hover:border-[#1f4d3a] hover:bg-transparent text-[#23211a]"
                >
                  {t('landing.startStarter') || 'Starter wählen'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* Solo Plan. The recommended single-user choice. Anchored
                with a strong green top rule + green tag + soft green
                shadow — the only card that "leans forward". */}
            <div className="bg-white border border-[#e3dccb] border-t-[3px] border-t-[#1f4d3a] p-7 relative flex flex-col shadow-[0_14px_36px_-20px_rgba(31,77,58,0.45)]">
              <div className="absolute top-4 right-4 px-2 py-0.5 bg-[#1f4d3a] rounded-[2px] text-[10px] font-semibold uppercase tracking-wider text-[#f7f3ea]">
                {t('landing.mostPopular') || 'Beliebteste Wahl'}
              </div>
              <div className="mb-5">
                <h3 className="text-xl font-bold text-[#23211a] mb-1">Autopilot</h3>
                <p className="text-sm text-[#6f6a5c]">
                  {t('landing.forSerious') || 'Posten läuft von allein'}
                </p>
              </div>
              <div className="mb-5">
                <span className="text-4xl sm:text-5xl font-bold text-[#23211a]">&euro;99</span>
                <span className="text-[#8a8474] text-sm"> / {t('pricing.month') || 'Monat'}</span>
              </div>
              <ul className="space-y-2.5 mb-6 flex-1">
                {soloFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2.5 text-sm text-[#4a463b]">
                    <Check className="w-4 h-4 text-[#1f4d3a] mt-0.5 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Link to="/auth" className="block mt-auto">
                <Button
                  data-testid="landing-pricing-solo"
                  className="rounded-[3px] w-full bg-[#1f4d3a] hover:bg-[#163a2c] text-[#f7f3ea]"
                >
                  {t('landing.startSolo') || 'Autopilot starten'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* Team Plan. The agency option. Teal accent matches the
                team-related UI elements throughout the app and visually
                differentiates from the violet Solo. */}
            <div className="bg-white border border-[#e3dccb] border-t-2 border-t-[#c7a869] p-7 relative flex flex-col">
              <div className="absolute top-4 right-4 px-2 py-0.5 rounded-[2px] text-[10px] font-semibold uppercase tracking-wider bg-[#faf6ec] text-[#8f6f3a] border border-[#c7a869]/60 inline-flex items-center gap-1">
                <Users className="w-3 h-3" />
                {t('landing.teamBadge') || 'Agentur'}
              </div>
              <div className="mb-5">
                <h3 className="text-xl font-bold text-[#23211a] mb-1">Team</h3>
                <p className="text-sm text-[#8a8474]">
                  {t('landing.forAgencies') || 'Der Autopilot fürs ganze Büro'}
                </p>
              </div>
              <div className="mb-1">
                <span className="text-4xl sm:text-5xl font-bold text-[#23211a]">&euro;179</span>
                <span className="text-[#8a8474] text-sm"> / {t('pricing.month') || 'Monat'}</span>
              </div>
              <p className="text-xs text-[#8f6f3a] mb-5">
                {t('landing.teamPriceHint') || '2 Plätze inklusive · jeder weitere +€89'}
              </p>
              <ul className="space-y-2.5 mb-4 flex-1">
                {teamFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2.5 text-sm text-[#4a463b]">
                    <Check className="w-4 h-4 text-[#8f6f3a] mt-0.5 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              {/* Savings comparison. A 4-person agency would pay
                  €396 for 4 separate Solo subs vs €357 on Team, so we
                  surface the saving to make the value-prop concrete. */}
              <div className="border-l-2 border-[#c7a869] pl-3 mb-5 text-xs">
                <p className="font-medium text-[#8f6f3a] mb-1">
                  {t('landing.teamSavingsTitle') || 'Beispiel: 4 Makler'}
                </p>
                <p className="text-[#6f6a5c]">
                  {t('landing.teamSavingsBody')
                    || '4× Autopilot = €396 / Monat · Team = €357 / Monat. Du sparst €39 / Monat (€468 / Jahr).'}
                </p>
              </div>
              <Link to="/auth" className="block mt-auto">
                <Button
                  data-testid="landing-pricing-team"
                  variant="outline"
                  className="w-full rounded-[3px] border-[#c7a869]/60 hover:border-[#8f6f3a] hover:bg-transparent text-[#23211a]"
                >
                  {t('landing.startTeam') || 'Team starten'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Inline reassurance under the pricing grid. The four most
              common follow-up questions, answered in one line each. The
              full FAQ lives at /faq with FAQPage schema for SEO; this is
              a curated subset for fence-sitters. */}
          <div className="mt-12 sm:mt-16 max-w-4xl">
            <h3 className="text-lg sm:text-xl font-semibold text-[#23211a] mb-5">
              {t('landing.pricingFaqTitle') || 'Häufige Fragen vor dem Start'}
            </h3>
            <div className="border-t border-[#e3dccb]">
              {[
                ['pricingFaq1Q', 'pricingFaq1A',
                  'Kann ich jederzeit kündigen?',
                  'Ja. Ein Klick im Stripe-Abrechnungsportal. Du behältst Zugriff bis zum Ende des bezahlten Zeitraums, danach wechselst du automatisch auf Free.'],
                ['pricingFaq2Q', 'pricingFaq2A',
                  'Was passiert mit meinen Videos, wenn ich kündige?',
                  'Bereits gerenderte Videos bleiben downloadbar. Du verlierst nur die Möglichkeit, neue auf deinem Limit zu erstellen.'],
                ['pricingFaq3Q', 'pricingFaq3A',
                  'Brauche ich eine Kreditkarte für Free?',
                  'Wir hinterlegen eine Karte zur Verifizierung (€0 Belastung). Anti-Missbrauch, damit Pro-Nutzer keine wartende Render-Schlange haben.'],
                ['pricingFaq4Q', 'pricingFaq4A',
                  'Kann ich von Autopilot auf Team wechseln?',
                  'Ja. Kündige Autopilot im Portal, starte das Team-Abo neu. Deine Immobilien & Videos bleiben erhalten.'],
              ].map(([qKey, aKey, qFallback, aFallback], i) => (
                <details
                  key={i}
                  className="group border-b border-[#e3dccb] py-4 cursor-pointer"
                >
                  <summary className="flex items-center justify-between gap-3 list-none">
                    <span className="text-sm font-medium text-[#23211a]">
                      {t(`landing.${qKey}`) || qFallback}
                    </span>
                    <ChevronRight className="w-4 h-4 text-[#9c9585] shrink-0 group-open:rotate-90 transition-transform" />
                  </summary>
                  <p className="text-sm text-[#6f6a5c] mt-3 leading-relaxed max-w-2xl">
                    {t(`landing.${aKey}`) || aFallback}
                  </p>
                </details>
              ))}
            </div>
            <p className="text-xs text-[#9c9585] mt-5">
              {t('landing.pricingFaqMorePrefix') || 'Mehr Fragen?'}{' '}
              <Link to="/faq" className="text-[#1f4d3a] hover:text-[#163a2c] underline underline-offset-4 decoration-[#1f4d3a]/40">
                {t('landing.pricingFaqMoreLink') || 'Komplette FAQ ansehen'}
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA. The one full-bleed deep-green band on the page:
          serif statement left, inverted ivory button right. Closes the
          Exposé the way a brochure back cover would. */}
      <section className="py-20 px-4 bg-[#1f4d3a]">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-3xl">
            <p className="kicker mb-4" style={{ color: '#c7a869' }}>06 · {t('landing.kickerStart') || 'Start'}</p>
            <h2 className="text-3xl md:text-5xl font-bold text-[#f7f3ea] mb-4 leading-tight">
              {t('landing.readyToTransform')}
            </h2>
            <p className="text-[#cfdcd0] text-lg">
              {t('landing.joinProfessionals')}
            </p>
          </div>
          <div className="shrink-0">
            <Link to="/auth">
              <Button
                data-testid="cta-get-started-button"
                className="rounded-[3px] bg-[#f7f3ea] hover:bg-white text-[#1f4d3a] font-semibold px-8 py-6 text-lg"
              >
                {t('landing.startFree')} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <p className="text-sm text-[#cfdcd0]/80 mt-4">{t('landing.noCreditCard')}</p>
          </div>
        </div>
      </section>

      {/* Footer. Branded landing version. The legal-link row uses the
          shared Footer component so the four required pages stay
          reachable from the public landing too. */}
      <footer className="py-10 px-4 border-t border-[#e3dccb] bg-[#f2ecdf]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <RCLogo size="sm" tone="ink" />
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-[#8a8474]">
            <a href="/impressum" className="hover:text-[#23211a] transition-colors">Impressum</a>
            <a href="/datenschutz" className="hover:text-[#23211a] transition-colors">{t('landing.privacy') || 'Datenschutz'}</a>
            <a href="/agb" className="hover:text-[#23211a] transition-colors">AGB</a>
            <a href="/widerrufsrecht" className="hover:text-[#23211a] transition-colors">Widerrufsrecht</a>
            <a href="/faq" className="hover:text-[#23211a] transition-colors">FAQ</a>
            <a href="mailto:support@real-content.at" className="hover:text-[#23211a] transition-colors">{t('landing.contact')}</a>
          </nav>
          <p className="text-sm text-[#9c9585]">&copy; {new Date().getFullYear()} Real Content</p>
        </div>
      </footer>
    </div>
  );
}

// ── Showcase gallery ─────────────────────────────────────────────────
// Each entry is a real Creatomate render of a different AI-generated
// property (Higgsfield Grok Imagine 2.0, one shared design bible per
// house so all rooms belong together). Photos live under
// uploads/demo-content/landing/<slug>/, renders under
// uploads/showcase/landing/<slug>.mp4 (see
// backend/creatomate_templates/render_landing_gallery.py).
// Bump GALLERY_VERSION after re-rendering so browsers drop cached MP4s.
const GALLERY_VERSION = '3';
const MEDIA_BASE = `${process.env.REACT_APP_BACKEND_URL || ''}/uploads`;
const GALLERY = [
  { slug: 'villa-doebling', poster: '01_aussen.jpg', template: 'Noir Luxus',
    title: 'Villa mit Weitblick', place: '1190 Wien', price: '€ 2.490.000' },
  { slug: 'familienhaus-baden', poster: '01_aussen.jpg', template: 'Plakat',
    title: 'Familienhaus mit Garten', place: '2500 Baden', price: '€ 685.000' },
  { slug: 'altbau-josefstadt', poster: '01_fassade.jpg', template: 'Cover Editorial',
    title: 'Altbau-Juwel', place: '1080 Wien', price: '€ 1.150.000' },
  { slug: 'penthouse-woerthersee', poster: '01_terrasse.jpg', template: 'Glass Panel',
    title: 'Penthouse am See', place: '9220 Velden', price: '€ 1.890.000' },
  { slug: 'chalet-kitzbuehel', poster: '01_aussen.jpg', template: 'Nordic Minimal',
    title: 'Chalet in den Bergen', place: '6370 Kitzbühel', price: '€ 3.200.000' },
  { slug: 'gartenwohnung-graz', poster: '01_garten.jpg', template: 'Klebeband Collage',
    title: 'Gartenwohnung mit Charme', place: '8010 Graz', price: '€ 389.000' },
  { slug: 'reihenhaus-linz', poster: '01_aussen.jpg', template: 'New Listing Story',
    title: 'Reihenhaus mit Garten', place: '4040 Linz', price: '€ 549.000' },
  { slug: 'dachwohnung-salzburg', poster: '01_terrasse.jpg', template: 'Swipe Up Picture',
    title: 'Dachgeschoss mit Terrasse', place: '5020 Salzburg', price: '€ 469.000' },
  { slug: 'landhaus-weinviertel', poster: '01_aussen.jpg', template: 'Bold New Listing',
    title: 'Renoviertes Landhaus', place: '2170 Poysdorf', price: '€ 329.000' },
];
const galleryVideoUrl = (item) =>
  `${MEDIA_BASE}/showcase/landing/${item.slug}.mp4?v=${GALLERY_VERSION}`;
const galleryPosterUrl = (item) =>
  `${MEDIA_BASE}/demo-content/landing/${item.slug}/${item.poster}`;

function GalleryVideo({ item, unmuted, onToggleSound }) {
  const ref = useRef(null);

  // Play only while at least half the frame is visible.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) el.play().catch(() => {});
        else el.pause();
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (ref.current) ref.current.muted = !unmuted;
  }, [unmuted]);

  return (
    <button
      type="button"
      onClick={onToggleSound}
      aria-label={unmuted ? 'Ton aus' : 'Ton an'}
      className="relative block w-full aspect-[9/16] overflow-hidden rounded-[2px] bg-[#efe9db] border border-[#e3dccb] shadow-[0_10px_30px_-18px_rgba(35,33,26,0.35)] cursor-pointer"
    >
      <video
        ref={ref}
        src={galleryVideoUrl(item)}
        poster={galleryPosterUrl(item)}
        className="w-full h-full object-cover"
        muted
        loop
        playsInline
        preload="metadata"
      />
      <span className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-black/45 text-white flex items-center justify-center">
        {unmuted ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
      </span>
    </button>
  );
}
